import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export const DEFAULT_LANGUAGE = 'en-US';
export const SUPPORTED_LANGUAGES = ['it-IT', 'en-US', 'el-GR', 'fr-FR', 'tr-TR', 'pl-PL'] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  // Chiave per salvare la lingua selezionata in localStorage
  private readonly storageKey = 'workinc-vet-trainees.language';

  // Lingua attiva mantenuta centralmente dal servizio
  private activeLanguage: SupportedLanguage = DEFAULT_LANGUAGE;

  constructor(
    private readonly translate: TranslateService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  // Configura l'inglese come lingua di riserva e ripristina l'ultima lingua salvata
  initialize(): void {
    this.translate.setFallbackLang(DEFAULT_LANGUAGE);
    this.setLanguage(this.readStoredLanguage() ?? DEFAULT_LANGUAGE, false);
  }

  // Restituisce la lingua attualmente utilizzata dall'applicazione
  get currentLanguage(): SupportedLanguage {
    return this.activeLanguage;
  }

  // Applica la lingua alle traduzioni e aggiorna l'attributo lang della pagina
  setLanguage(language: SupportedLanguage, persist = true): void {
    this.activeLanguage = language;
    this.translate.use(language);
    this.document.documentElement.lang = language;

    // Salva la scelta in localStorage solo nel browser e quando richiesto
    if (persist && isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(this.storageKey, language);
      } catch {
        // La lingua rimane attiva anche quando il salvataggio nel browser non è disponibile
      }
    }
  }

  // Recupera da localStorage la lingua salvata verificando che sia supportata
  private readStoredLanguage(): SupportedLanguage | null {
    // Durante il rendering lato server localStorage non è disponibile
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const storedLanguage = localStorage.getItem(this.storageKey);
      return SUPPORTED_LANGUAGES.find(language => language === storedLanguage) ?? null;
    } catch {
      return null;
    }
  }
}
